import { useState, useCallback } from "react";
import { Copy, Check, RefreshCw, Globe, ShieldCheck, Key, AlertTriangle, Send } from "lucide-react";
import { apiBaseUrl } from "@/shared/lib/urls";
import AutomationService from "../../services/AutomationService";
import type { WebhookSecret } from "../../models/WebhookSecret";

interface WebhookPanelProps {
  workflowId: string;
  isNew: boolean;
}

const service = new AutomationService();

export default function WebhookPanel({ workflowId, isNew }: WebhookPanelProps) {
  const [secret, setSecret] = useState<WebhookSecret | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState<"url" | "token" | "curl" | "directUrl" | "telegram" | null>(null);

  const webhookUrl = `${apiBaseUrl()}/api/webhooks/${workflowId}`;

  const generateToken = useCallback(async () => {
    setLoading(true);
    try {
      const result = await service.CreateOrRotateWebhook(workflowId);
      setSecret(result);
    } catch {
      // error handled by ApiAcces interceptor
    } finally {
      setLoading(false);
    }
  }, [workflowId]);

  const copyToClipboard = async (text: string, key: "url" | "token" | "curl" | "directUrl" | "telegram") => {
    await navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const curlExample = secret
    ? `curl -X POST '${webhookUrl}' \\\n  -H 'Content-Type: application/json' \\\n  -H 'X-Webhook-Token: ${secret.token}' \\\n  -H 'Idempotency-Key: unique-key-123' \\\n  -d '{ "example": "data" }'`
    : null;

  if (isNew) {
    return (
      <div className="border border-amber-500/20 bg-amber-500/5 rounded-xl p-4 space-y-2">
        <div className="flex items-center gap-2 text-amber-600">
          <AlertTriangle className="size-4" />
          <p className="text-sm font-semibold">Guarda primero</p>
        </div>
        <p className="text-xs text-foreground/50 leading-relaxed">
          Guarda la automatización antes de generar el token del webhook.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Webhook URL */}
      <div className="space-y-2">
        <p className="text-xs font-bold uppercase tracking-wider text-foreground/40 flex items-center gap-1.5">
          <Globe className="size-3" />
          URL del Webhook
        </p>
        <div className="flex items-center gap-2">
          <code className="flex-1 text-xs bg-black/[0.03] border border-black/5 rounded-lg px-3 py-2.5 font-mono text-foreground/70 break-all select-all">
            {webhookUrl}
          </code>
          <button
            type="button"
            onClick={() => copyToClipboard(webhookUrl, "url")}
            className="p-2 rounded-lg hover:bg-black/5 text-foreground/40 hover:text-foreground transition-colors shrink-0"
            title="Copiar URL"
          >
            {copied === "url" ? <Check className="size-4 text-green-500" /> : <Copy className="size-4" />}
          </button>
        </div>
      </div>

      {/* Token section */}
      <div className="space-y-2">
        <p className="text-xs font-bold uppercase tracking-wider text-foreground/40 flex items-center gap-1.5">
          <Key className="size-3" />
          Token de autenticación
        </p>

        {secret ? (
          <div className="space-y-3">
            <div className="border border-green-500/20 bg-green-500/5 rounded-xl p-3 space-y-2">
              <div className="flex items-center gap-2">
                <ShieldCheck className="size-4 text-green-600 shrink-0" />
                <p className="text-xs font-semibold text-green-700">Token generado</p>
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-white/80 border border-green-500/10 rounded-lg px-3 py-2 font-mono text-foreground/80 break-all select-all">
                  {secret.token}
                </code>
                <button
                  type="button"
                  onClick={() => copyToClipboard(secret.token, "token")}
                  className="p-2 rounded-lg hover:bg-green-500/10 text-foreground/40 hover:text-green-600 transition-colors shrink-0"
                  title="Copiar token"
                >
                  {copied === "token" ? <Check className="size-4 text-green-500" /> : <Copy className="size-4" />}
                </button>
              </div>
              <p className="text-[11px] text-amber-600 font-medium flex items-center gap-1">
                <AlertTriangle className="size-3" />
                Copia este token ahora. No podrás verlo de nuevo.
              </p>
            </div>

            {/* Direct URL for providers that can't send custom headers (Telegram, etc.) */}
            <div className="space-y-1.5">
              <p className="text-[11px] font-semibold text-foreground/40 uppercase tracking-wider">
                URL directa (proveedores externos)
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-black/[0.03] border border-black/5 rounded-lg px-3 py-2.5 font-mono text-foreground/70 break-all select-all">
                  {`${webhookUrl}?token=${secret.token}`}
                </code>
                <button
                  type="button"
                  onClick={() => copyToClipboard(`${webhookUrl}?token=${secret.token}`, "directUrl")}
                  className="p-2 rounded-lg hover:bg-black/5 text-foreground/40 hover:text-foreground transition-colors shrink-0"
                  title="Copiar URL directa"
                >
                  {copied === "directUrl" ? <Check className="size-4 text-green-500" /> : <Copy className="size-4" />}
                </button>
              </div>
              <p className="text-[11px] text-foreground/40 leading-relaxed">
                Pégala tal cual en servicios que no permiten headers personalizados (Telegram
                setWebhook, GitLab, etc.). El <code className="px-1 bg-black/5 rounded font-mono">Idempotency-Key</code> es
                opcional en este modo: se deriva automáticamente del contenido de cada petición.
              </p>
            </div>

            {/* Telegram registration example (the most common no-custom-headers provider) */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-semibold text-foreground/40 uppercase tracking-wider flex items-center gap-1">
                  <Send className="size-3" />
                  Registrar en Telegram
                </p>
                <button
                  type="button"
                  onClick={() =>
                    copyToClipboard(
                      `curl -F "url=${webhookUrl}?token=${secret.token}" "https://api.telegram.org/bot<BOT_TOKEN>/setWebhook"`,
                      "telegram",
                    )
                  }
                  className="flex items-center gap-1 text-[11px] font-semibold text-foreground/40 hover:text-accent transition-colors"
                >
                  {copied === "telegram" ? <Check className="size-3 text-green-500" /> : <Copy className="size-3" />}
                  {copied === "telegram" ? "Copiado" : "Copiar"}
                </button>
              </div>
              <pre className="text-[11px] bg-gray-900 text-gray-100 rounded-xl px-4 py-3 font-mono overflow-x-auto whitespace-pre leading-relaxed">
                {`curl -F "url=${webhookUrl}?token=${secret.token}" \\\n  "https://api.telegram.org/bot<BOT_TOKEN>/setWebhook"`}
              </pre>
              <p className="text-[11px] text-foreground/40 leading-relaxed">
                Reemplaza <code className="px-1 bg-black/5 rounded font-mono">&lt;BOT_TOKEN&gt;</code> por el token de tu
                bot. Verifica el registro con{" "}
                <code className="px-1 bg-black/5 rounded font-mono">getWebhookInfo</code>: sin{" "}
                <code className="px-1 bg-black/5 rounded font-mono">last_error_message</code> significa que Telegram ya
                entrega los updates aquí.
              </p>
            </div>

            {/* curl example */}
            {curlExample && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] font-semibold text-foreground/40 uppercase tracking-wider">Ejemplo</p>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(curlExample, "curl")}
                    className="flex items-center gap-1 text-[11px] font-semibold text-foreground/40 hover:text-accent transition-colors"
                  >
                    {copied === "curl" ? <Check className="size-3 text-green-500" /> : <Copy className="size-3" />}
                    {copied === "curl" ? "Copiado" : "Copiar"}
                  </button>
                </div>
                <pre className="text-[11px] bg-gray-900 text-gray-100 rounded-xl px-4 py-3 font-mono overflow-x-auto whitespace-pre leading-relaxed">
                  {curlExample}
                </pre>
              </div>
            )}

            <button
              onClick={generateToken}
              disabled={loading}
              className="w-full h-10 rounded-xl border border-accent/30 bg-white/60 text-accent text-sm font-semibold flex items-center justify-center gap-2 hover:bg-accent/5 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`size-4 ${loading ? "animate-spin" : ""}`} />
              Rotar token
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-foreground/50 leading-relaxed">
              Genera un token para autenticar las peticiones al webhook. Envíalo en el header
              <code className="mx-1 px-1.5 py-0.5 bg-black/5 rounded text-[11px] font-mono">X-Webhook-Token</code>
              junto con un
              <code className="mx-1 px-1.5 py-0.5 bg-black/5 rounded text-[11px] font-mono">Idempotency-Key</code>
              único por petición.
            </p>
            <button
              onClick={generateToken}
              disabled={loading}
              className="w-full h-10 rounded-xl bg-accent text-white text-sm font-semibold flex items-center justify-center gap-2 shadow-lg shadow-accent/25 hover:bg-accent/90 hover:shadow-accent/35 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Key className={`size-4 ${loading ? "animate-spin" : ""}`} />
              Generar token
            </button>
          </div>
        )}
      </div>

      {/* Headers info */}
      <div className="border border-black/5 rounded-xl bg-black/[0.02] p-3">
        <p className="text-[11px] font-bold text-foreground/40 uppercase tracking-wider mb-2">Headers requeridos</p>
        <div className="space-y-1">
          <div className="flex items-baseline gap-2">
            <code className="text-[11px] font-mono text-accent font-semibold">X-Webhook-Token</code>
            <span className="text-[11px] text-foreground/40">— Token de autenticación (o ?token= en la URL)</span>
          </div>
          <div className="flex items-baseline gap-2">
            <code className="text-[11px] font-mono text-accent font-semibold">Idempotency-Key</code>
            <span className="text-[11px] text-foreground/40">— Clave única por petición (dedup 24h; opcional, se deriva del body)</span>
          </div>
          <div className="flex items-baseline gap-2">
            <code className="text-[11px] font-mono text-accent font-semibold">Content-Type</code>
            <span className="text-[11px] text-foreground/40">— application/json</span>
          </div>
        </div>
      </div>
    </div>
  );
}
